import React from 'react';
import { MotionConfig } from 'motion/react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { PrivacyProvider } from './contexts/PrivacyContext';
import { ProjectProvider } from './contexts/ProjectContext';
import { RealtimeProvider } from './contexts/RealtimeContext';
import { AppLayout } from './components/layout/AppLayout';

import { OverviewPage } from './pages/OverviewPage';
import { ProjectsPage } from './pages/ProjectsPage';
import { ProjectDetailPage } from './pages/ProjectDetailPage';
import { ConsolePage } from './pages/ConsolePage';
import { AssistantPage } from './pages/AssistantPage';
import { ChatGPTPage } from './pages/ChatGPTPage';
import { RemoteDesktopPage } from './pages/RemoteDesktopPage';
import { FilesPage } from './pages/FilesPage';
import { DeploymentsPage } from './pages/DeploymentsPage';
import { HistoryPage } from './pages/HistoryPage';
import { SettingsPage } from './pages/SettingsPage';
import { LoginPage } from './pages/LoginPage';

const ProtectedRoute: React.FC<{ children: React.ReactElement }> = ({ children }) => {
  const { user, isLoading } = useAuth();

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#060b16] text-slate-500 font-mono text-xs">
        Loading KAIZEN Remote Cockpit...
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  return children;
};

export const App: React.FC = () => {
  return (
    <MotionConfig reducedMotion="user">
      <BrowserRouter>
      <AuthProvider>
        <PrivacyProvider>
          <ProjectProvider>
            <RealtimeProvider>
              <Routes>
                <Route path="/login" element={<LoginPage />} />
                <Route
                  path="/"
                  element={
                    <ProtectedRoute>
                      <AppLayout />
                    </ProtectedRoute>
                  }
                >
                  <Route index element={<OverviewPage />} />
                  <Route path="projects" element={<ProjectsPage />} />
                  <Route path="projects/:id" element={<ProjectDetailPage />} />
                  <Route path="console" element={<ConsolePage />} />
                  <Route path="assistant" element={<AssistantPage />} />
                  <Route path="chatgpt" element={<ChatGPTPage />} />
                  <Route path="remote-desktop" element={<RemoteDesktopPage />} />
                  <Route path="files" element={<FilesPage />} />
                  <Route path="deployments" element={<DeploymentsPage />} />
                  <Route path="history" element={<HistoryPage />} />
                  <Route path="settings" element={<SettingsPage />} />
                </Route>
                <Route path="*" element={<Navigate to="/" replace />} />
              </Routes>
            </RealtimeProvider>
          </ProjectProvider>
        </PrivacyProvider>
      </AuthProvider>
      </BrowserRouter>
    </MotionConfig>
  );
};

